/**
 * Reveal.js PDF / handout export: load print CSS, handout layout
 * (slide in overview-style frame + notes beside), strip Lectura [[...]] codewords.
 *
 * Public URL flag is `?summary`. Reveal core still expects `print-pdf` in location.search
 * (see isPrintingPDF() in reveal.js); we append it in ensureRevealSummaryPrintPdfQuery().
 */

import * as QRiousModule from 'qrious';

import { isRevealPrintPdfMode } from './reveal-print-core';

export {
  ensureRevealSummaryPrintPdfQuery,
  injectRevealPrintPdfStyles,
  isRevealPrintPdfMode
} from './reveal-print-core';

interface QRiousCtor {
  new (opts: {
    element: HTMLCanvasElement;
    value: string;
    size?: number;
    padding?: number;
    foreground?: string;
    background?: string;
    level?: 'L' | 'M' | 'Q' | 'H';
  }): unknown;
}

function getQRiousConstructor(): QRiousCtor | null {
  const fromModule =
    (QRiousModule as unknown as { default?: unknown }).default ?? (QRiousModule as unknown);
  return typeof fromModule === 'function' ? (fromModule as QRiousCtor) : null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getNotesHighlightRgbTuple(): [number, number, number] {
  try {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--notes-tts-highlight-rgb')
      .trim();
    const parts = raw.split(',').map((x) => parseInt(x.trim(), 10));
    if (parts.length >= 3 && parts.every((n) => Number.isFinite(n))) {
      return [parts[0]!, parts[1]!, parts[2]!];
    }
  } catch {
    /* ignore */
  }
  return [124, 58, 237];
}

function getShareUrlForHandout(): string {
  try {
    const u = new URL(window.location.href);
    u.searchParams.delete('print-pdf');
    u.searchParams.delete('summary');
    return u.toString();
  } catch {
    return window.location.href.split(/[?#]/)[0] || window.location.href;
  }
}

function inferDeckTitle(): string {
  const t = document.querySelector('title')?.textContent?.replace(/\s+/g, ' ').trim();
  if (t) {
    const first = t.split(/\s*[|\u2013\u2014-]\s*/)[0]?.trim();
    return first || t;
  }
  const h = document.querySelector('.reveal .slides section h1, .reveal .slides section h2') as
    | HTMLElement
    | null;
  const ht = h?.textContent?.replace(/\s+/g, ' ').trim();
  return ht || 'Presentation';
}

function getOptionalMetaDescription(): string {
  const m = document.querySelector('meta[name="description"]')?.getAttribute('content');
  const s = (m || '').replace(/\s+/g, ' ').trim();
  if (s.length > 220) return `${s.slice(0, 217)}…`;
  return s;
}

function getViewerDisplayName(): string {
  try {
    const w = window as unknown as {
      LecturaNext?: {
        getCurrentUser?: () => {
          displayName?: string;
          email?: string;
          isAnonymous?: boolean;
        } | null;
      };
    };
    const u = w.LecturaNext?.getCurrentUser?.();
    if (!u || u.isAnonymous) return '';
    const n = (u.displayName || '').trim();
    if (n) return n;
    const e = (u.email || '').trim();
    if (e) {
      const local = e.split('@')[0];
      return (local || e).trim();
    }
  } catch {
    /* ignore */
  }
  return '';
}

function countVisibleHandoutPdfPages(): number {
  return document.querySelectorAll(
    '.reveal .slides .pdf-page:not(.lectura-print-filtered-out)'
  ).length;
}

/** Slides with non-empty personal note export (after data-lectura-has-personal-note is set). */
function countVisiblePersonalNotesStats(): { withPersonalNotes: number; slideTotal: number } {
  const pages = document.querySelectorAll(
    '.reveal .slides .pdf-page:not(.lectura-print-filtered-out)'
  );
  let withPersonalNotes = 0;
  for (let i = 0; i < pages.length; i++) {
    if (pages[i]!.getAttribute('data-lectura-has-personal-note') === '1') withPersonalNotes++;
  }
  return { withPersonalNotes, slideTotal: pages.length };
}

/** Summary cover block (first row of lead sheet); not a full `.lectura-print-sheet`. */
function buildHandoutCoverBand(): HTMLElement {
  const band = document.createElement('div');
  band.className = 'lectura-print-cover-band';
  band.id = 'lectura-print-cover-root';

  const title = inferDeckTitle();
  const desc = getOptionalMetaDescription();
  const shareUrl = getShareUrlForHandout();
  const viewer = getViewerDisplayName();
  const slideCount = countVisibleHandoutPdfPages();
  const { withPersonalNotes } = countVisiblePersonalNotesStats();
  const notesHydrated = document.documentElement.classList.contains('lectura-print-personal-notes');

  const [r, g, b] = getNotesHighlightRgbTuple();
  const accentRgb = `${r}, ${g}, ${b}`;

  const row = document.createElement('div');
  row.className = 'lectura-print-cover-row';

  const mainCol = document.createElement('div');
  mainCol.className = 'lectura-print-cover__main-col';

  const intro = document.createElement('div');
  intro.className = 'lectura-print-cover__intro';

  const brand = document.createElement('div');
  brand.className = 'lectura-print-cover__brand';
  const logoWrap = document.createElement('div');
  logoWrap.className = 'lectura-print-cover__logo-wrap';
  const logo = document.createElement('img');
  logo.className = 'lectura-print-cover__logo';
  logo.alt = 'Lectura';
  const setLogoFallbackSvg = (): void => {
    try {
      logo.src = new URL('/favicon.svg', window.location.origin).href;
    } catch {
      logo.src = '/favicon.svg';
    }
    logo.classList.add('lectura-print-cover__logo--force-dark');
  };
  try {
    logo.src = new URL('/favicon-96x96.png', window.location.origin).href;
  } catch {
    logo.src = '/favicon-96x96.png';
  }
  logo.addEventListener('error', setLogoFallbackSvg, { once: true });
  logoWrap.appendChild(logo);
  const brandText = document.createElement('div');
  brandText.className = 'lectura-print-cover__brand-text';
  brandText.innerHTML =
    '<span class="lectura-print-cover__brand-name">Lectura</span><span class="lectura-print-cover__brand-tag">Summary</span>';
  brand.appendChild(logoWrap);
  brand.appendChild(brandText);

  const h1 = document.createElement('h1');
  h1.className = 'lectura-print-cover__title';
  h1.textContent = title;
  intro.appendChild(brand);
  intro.appendChild(h1);
  if (desc) {
    const p = document.createElement('p');
    p.className = 'lectura-print-cover__desc';
    p.textContent = desc;
    intro.appendChild(p);
  }
  mainCol.appendChild(intro);

  const meta = document.createElement('div');
  meta.className = 'lectura-print-cover__meta';
  const dateStr = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const slidesLine =
    slideCount === 1 ? '1 slide' : `${slideCount} slides`;
  const notesValue = notesHydrated
    ? withPersonalNotes === 0
      ? 'None on this deck yet'
      : withPersonalNotes === 1
        ? '1 slide includes your notes'
        : `${withPersonalNotes} slides include your notes`
    : 'Loading…';
  const metaRows: string[] = [];
  if (viewer) {
    metaRows.push(
      `<div class="lectura-print-cover__meta-item lectura-print-cover__meta-item--hero"><span class="lectura-print-cover__meta-label">Prepared for</span><span class="lectura-print-cover__meta-value lectura-print-cover__meta-value--hero">${escapeHtml(viewer)}</span></div>`
    );
  } else {
    metaRows.push(
      '<div class="lectura-print-cover__meta-item"><span class="lectura-print-cover__meta-label">Prepared for</span><span class="lectura-print-cover__meta-value lectura-print-cover__meta-muted">Sign in to Lectura to show your name here.</span></div>'
    );
  }
  metaRows.push(
    `<div class="lectura-print-cover__meta-item"><span class="lectura-print-cover__meta-label">Deck</span><span class="lectura-print-cover__meta-value">${escapeHtml(slidesLine)}</span></div>`
  );
  metaRows.push(
    `<div class="lectura-print-cover__meta-item"><span class="lectura-print-cover__meta-label">Your notes</span><span class="lectura-print-cover__meta-value">${escapeHtml(notesValue)}</span></div>`
  );
  metaRows.push(
    `<div class="lectura-print-cover__meta-item"><span class="lectura-print-cover__meta-label">Printed</span><span class="lectura-print-cover__meta-value lectura-print-cover__meta-value--subtle">${escapeHtml(dateStr)}</span></div>`
  );
  meta.innerHTML = metaRows.join('');
  mainCol.appendChild(meta);

  const sideCol = document.createElement('aside');
  sideCol.className = 'lectura-print-cover__side-col';
  const sideHead = document.createElement('div');
  sideHead.className = 'lectura-print-cover__side-head';
  sideHead.textContent = 'Open presentation';
  sideCol.appendChild(sideHead);

  const QRious = getQRiousConstructor();
  if (QRious) {
    const canvas = document.createElement('canvas');
    new QRious({
      element: canvas,
      value: shareUrl,
      size: 168,
      padding: 10,
      foreground: `rgb(${accentRgb})`,
      background: '#ffffff',
      level: 'M'
    });
    const qrImg = document.createElement('img');
    qrImg.className = 'lectura-print-cover__qr-img';
    qrImg.alt = 'QR code linking to the presentation';
    qrImg.src = canvas.toDataURL('image/png');
    const qrWrap = document.createElement('div');
    qrWrap.className = 'lectura-print-cover__qr-wrap';
    qrWrap.appendChild(qrImg);
    sideCol.appendChild(qrWrap);
  }

  const urlLine = document.createElement('p');
  urlLine.className = 'lectura-print-cover__url';
  urlLine.textContent = shareUrl;
  sideCol.appendChild(urlLine);

  const notesDetail = document.createElement('div');
  notesDetail.className = 'lectura-print-cover__notes-detail';
  notesDetail.innerHTML = notesHydrated
    ? `<span class="lectura-print-cover__notes-detail-label">Personal notes</span><span class="lectura-print-cover__notes-detail-value">${withPersonalNotes} slide${withPersonalNotes === 1 ? '' : 's'} with your text</span>`
    : '<span class="lectura-print-cover__notes-detail-muted">Personal note counts update after notes load.</span>';
  sideCol.appendChild(notesDetail);

  row.appendChild(mainCol);
  row.appendChild(sideCol);
  band.appendChild(row);
  return band;
}

/** Rebuild cover band inside the lead sheet (after notes hydrate / filter / print). */
function refreshHandoutLeadCover(): void {
  const lead = document.querySelector('.lectura-print-sheet--lead');
  if (!lead) return;
  document.getElementById('lectura-print-cover-root')?.remove();
  lead.insertBefore(buildHandoutCoverBand(), lead.firstChild);
}

/** Strip Lectura TTS / remote codewords from printed notes for a readable handout. */
export function stripLecturaCodewordsFromHtml(html: string): string {
  if (!html || typeof html !== 'string') return html;
  return html.replace(/\[\[[^\]]+\]\]/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Move slide background + section into a fixed 16:9 frame; notes into a column beside it
 * (like overview thumbnails next to content, but stacked vertically per slide).
 *
 * Slide scaler uses Reveal config width × height (native slide coordinate space), same as
 * overview/PDF layout — not viewport-scaled getComputedSlideSize or offsetWidth.
 */
function restructurePdfPagesToHandout(nativeSlideW: number, nativeSlideH: number): void {
  document.querySelectorAll('.reveal .slides .pdf-page').forEach((page) => {
    const p = page as HTMLElement;
    const bg = p.querySelector(':scope > .slide-background') as HTMLElement | null;
    const sec = p.querySelector(':scope > section') as HTMLElement | null;
    const notes = p.querySelector(':scope > .speaker-notes-pdf') as HTMLElement | null;
    const num = p.querySelector(':scope > .slide-number-pdf') as HTMLElement | null;
    if (!sec) return;

    const row = document.createElement('div');
    row.className = 'lectura-print-handout-row';
    const frame = document.createElement('div');
    frame.className = 'lectura-print-slide-frame';
    const notesCol = document.createElement('div');
    notesCol.className = 'lectura-print-notes-col';

    const scaler = document.createElement('div');
    scaler.className = 'lectura-print-slide-scaler';
    // Reveal lays out slide content in config width × height (e.g. 1280×720), not viewport-scaled
    // getComputedSlideSize — using native size matches overview/PDF coordinate space.
    scaler.dataset.lecturaSlideW = String(nativeSlideW);
    scaler.dataset.lecturaSlideH = String(nativeSlideH);

    if (bg) scaler.appendChild(bg);
    scaler.appendChild(sec);
    frame.appendChild(scaler);
    if (notes) notesCol.appendChild(notes);
    else row.classList.add('lectura-print-handout-row--no-notes');
    row.appendChild(frame);
    row.appendChild(notesCol);

    while (p.firstChild) p.removeChild(p.firstChild);
    p.appendChild(row);
    if (num) p.appendChild(num);
  });
}

/** Undo sheet pairing so `.pdf-page` are direct children of `.slides` again (for refilter / regroup). */
function unwrapHandoutSheets(): void {
  const slides = document.querySelector('.reveal .slides');
  if (!slides) return;
  const sheets = Array.from(slides.querySelectorAll(':scope > .lectura-print-sheet'));
  for (const sheet of sheets) {
    sheet.querySelector('#lectura-print-cover-root')?.remove();
    const pages = Array.from(sheet.querySelectorAll(':scope > .pdf-page'));
    for (const p of pages) {
      slides.insertBefore(p, sheet);
    }
    sheet.remove();
  }
}

/**
 * Pair slides into A4 sheets: first physical page = summary cover (top half) + first slide (bottom half);
 * later sheets = two handout rows per page.
 */
function groupHandoutPagesIntoSheets(): void {
  const slides = document.querySelector('.reveal .slides');
  if (!slides) return;
  const allPages = Array.from(slides.querySelectorAll(':scope > .pdf-page')) as HTMLElement[];
  if (allPages.length === 0) return;
  const visible = allPages.filter((p) => !p.classList.contains('lectura-print-filtered-out'));
  const hidden = allPages.filter((p) => p.classList.contains('lectura-print-filtered-out'));
  for (const p of hidden) {
    slides.appendChild(p);
  }
  if (visible.length === 0) return;

  const leadSheet = document.createElement('div');
  leadSheet.className = 'lectura-print-sheet lectura-print-sheet--lead';
  const first = visible[0]!;
  slides.insertBefore(leadSheet, first);
  leadSheet.appendChild(buildHandoutCoverBand());
  leadSheet.appendChild(first);

  for (let i = 1; i < visible.length; i += 2) {
    const sheet = document.createElement('div');
    sheet.className = 'lectura-print-sheet';
    const a = visible[i]!;
    slides.insertBefore(sheet, a);
    sheet.appendChild(a);
    if (visible[i + 1]) sheet.appendChild(visible[i + 1]!);
  }
}

let handoutLayoutRunner: (() => void) | null = null;

/**
 * Rebuild sheets after filtering personal notes. Safe to call before Firestore hydration
 * (pages without data-lectura-has-personal-note are not hidden when filter is on).
 */
export function applyPrintPdfNotesFilter(onlyWithNotes: boolean): void {
  if (!isRevealPrintPdfMode()) return;
  document.documentElement.classList.toggle('lectura-print-filter-only-notes', onlyWithNotes);
  try {
    sessionStorage.setItem('lectura-print-only-personal-notes', onlyWithNotes ? '1' : '0');
  } catch {
    /* ignore */
  }

  document.querySelectorAll('.reveal .slides .pdf-page').forEach((page) => {
    const p = page as HTMLElement;
    const v = p.getAttribute('data-lectura-has-personal-note');
    const hide = onlyWithNotes && v === '0';
    p.classList.toggle('lectura-print-filtered-out', hide);
  });

  unwrapHandoutSheets();
  groupHandoutPagesIntoSheets();
  clearRevealPdfPageHeights();
  handoutLayoutRunner?.();
}

const TOOLBAR_FILTER_ICONS = {
  filter:
    '<svg class="lectura-print-toolbar__icon-svg" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" aria-hidden="true"><path stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 2v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>',
  print:
    '<svg class="lectura-print-toolbar__icon-svg" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" aria-hidden="true"><path stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/></svg>',
  close:
    '<svg class="lectura-print-toolbar__icon-svg" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" aria-hidden="true"><path stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>'
};

function wirePrintToolbarLogo(img: HTMLImageElement): void {
  try {
    img.src = new URL('/favicon-96x96.png', window.location.origin).href;
  } catch {
    img.src = '/favicon-96x96.png';
  }
  img.addEventListener(
    'error',
    () => {
      try {
        img.src = new URL('/favicon.svg', window.location.origin).href;
      } catch {
        img.src = '/favicon.svg';
      }
      img.classList.add('lectura-print-toolbar__logo--dark');
    },
    { once: true }
  );
}

function installPrintPdfToolbar(): void {
  if (document.getElementById('lectura-print-toolbar')) return;

  const bar = document.createElement('div');
  bar.id = 'lectura-print-toolbar';
  bar.className = 'lectura-print-toolbar';
  bar.setAttribute('role', 'banner');
  bar.setAttribute('aria-label', 'Print summary');
  bar.innerHTML = `
    <div class="lectura-print-toolbar__inner">
      <div class="lectura-print-toolbar__left">
        <div class="lectura-print-toolbar__brand">
          <img class="lectura-print-toolbar__logo" src="" width="28" height="28" alt="" decoding="async" />
        </div>
        <span class="lectura-print-toolbar__title">Summary</span>
      </div>
      <div class="lectura-print-toolbar__filter-slot">
        <div class="lectura-print-toolbar__filter-anchor">
          <button type="button" class="lectura-print-toolbar__btn lectura-print-toolbar__btn--icon lectura-print-toolbar__btn--filter" id="lectura-print-filter-btn"
            aria-expanded="false" aria-haspopup="dialog" aria-controls="lectura-print-filter-popover" title="Filter">
            ${TOOLBAR_FILTER_ICONS.filter}
            <span class="lectura-print-toolbar__sr-only">Filter</span>
          </button>
          <div class="lectura-print-toolbar__popover" id="lectura-print-filter-popover" role="dialog" aria-label="Filter summary" hidden>
            <div class="lectura-print-toolbar__popover-arrow" aria-hidden="true"></div>
            <div class="lectura-print-toolbar__popover-card">
              <p class="lectura-print-toolbar__popover-kicker">Visibility</p>
              <h2 class="lectura-print-toolbar__popover-heading">Which slides to include</h2>
              <label class="lectura-print-toolbar__toggle-row" for="lectura-print-filter-notes">
                <span class="lectura-print-toolbar__toggle-text">
                  <span class="lectura-print-toolbar__toggle-title">Notes only</span>
                  <span class="lectura-print-toolbar__toggle-desc">Show slides where you saved personal notes in the drawer.</span>
                </span>
                <span class="lectura-print-toolbar__switch">
                  <input type="checkbox" id="lectura-print-filter-notes" class="lectura-print-toolbar__switch-input" disabled />
                  <span class="lectura-print-toolbar__switch-track" aria-hidden="true"></span>
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="lectura-print-toolbar__actions">
        <button type="button" class="lectura-print-toolbar__btn lectura-print-toolbar__btn--icon" id="lectura-print-scroll-top" title="Scroll to top" aria-label="Scroll to top">↑</button>
        <button type="button" class="lectura-print-toolbar__btn lectura-print-toolbar__btn--icon lectura-print-toolbar__btn--print" id="lectura-print-print-btn" title="Print" aria-label="Print">
          ${TOOLBAR_FILTER_ICONS.print}
        </button>
        <button type="button" class="lectura-print-toolbar__btn lectura-print-toolbar__btn--icon lectura-print-toolbar__btn--close" id="lectura-print-exit-btn" title="Close summary" aria-label="Close summary">
          ${TOOLBAR_FILTER_ICONS.close}
        </button>
      </div>
    </div>
  `;
  document.body.insertBefore(bar, document.body.firstChild);
  document.documentElement.classList.add('lectura-print-toolbar-visible');

  const logo = bar.querySelector('.lectura-print-toolbar__logo') as HTMLImageElement;
  if (logo) {
    logo.alt = 'Lectura';
    wirePrintToolbarLogo(logo);
  }

  const syncToolbarInset = (): void => {
    const h = Math.ceil(bar.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--lectura-print-toolbar-offset', `${h}px`);
  };
  syncToolbarInset();
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => syncToolbarInset());
    ro.observe(bar);
  }
  window.addEventListener('resize', syncToolbarInset, { passive: true });

  const filterBtn = bar.querySelector('#lectura-print-filter-btn') as HTMLButtonElement;
  const popover = bar.querySelector('#lectura-print-filter-popover') as HTMLElement;
  const filterAnchor = bar.querySelector('.lectura-print-toolbar__filter-anchor') as HTMLElement;
  const filterInput = bar.querySelector('#lectura-print-filter-notes') as HTMLInputElement;

  const setPopoverOpen = (open: boolean): void => {
    popover.hidden = !open;
    filterBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    filterBtn.classList.toggle('lectura-print-toolbar__btn--filter-active', open);
  };

  const syncFilterFromStorage = (): void => {
    try {
      const s = sessionStorage.getItem('lectura-print-only-personal-notes');
      if (s === '1') {
        filterInput.checked = true;
        applyPrintPdfNotesFilter(true);
      }
    } catch {
      /* ignore */
    }
  };

  filterBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    setPopoverOpen(popover.hidden);
  });

  const onDocPointerDown = (e: MouseEvent | TouchEvent): void => {
    if (popover.hidden) return;
    const t = e.target as Node;
    if (filterAnchor.contains(t)) return;
    setPopoverOpen(false);
  };
  document.addEventListener('mousedown', onDocPointerDown, true);
  document.addEventListener('touchstart', onDocPointerDown, true);

  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Escape' && !popover.hidden) {
        setPopoverOpen(false);
        filterBtn.focus();
      }
    },
    true
  );

  bar.querySelector('#lectura-print-print-btn')?.addEventListener('click', () => {
    window.print();
  });
  bar.querySelector('#lectura-print-scroll-top')?.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
  bar.querySelector('#lectura-print-exit-btn')?.addEventListener('click', () => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('print-pdf');
      u.searchParams.delete('summary');
      window.location.href = u.toString();
    } catch {
      window.history.back();
    }
  });
  filterInput.addEventListener('change', () => {
    applyPrintPdfNotesFilter(filterInput.checked);
  });

  window.addEventListener('lectura-print-personal-notes-hydrated', ((ev: Event) => {
    if (
      !(ev as CustomEvent<{ totalSlides: number; withPersonalNotes: number }>).detail
    ) {
      return;
    }
    filterInput.disabled = false;
    syncFilterFromStorage();
    refreshHandoutLeadCover();
    handoutLayoutRunner?.();
  }) as EventListener);

  requestAnimationFrame(() => syncToolbarInset());
}

/** Reveal sets a tall inline height on each .pdf-page; drop it so sheet grid can size rows. */
function clearRevealPdfPageHeights(): void {
  document.querySelectorAll('.reveal .slides .pdf-page').forEach((el) => {
    const p = el as HTMLElement;
    p.style.removeProperty('height');
    p.style.removeProperty('min-height');
  });
}

/**
 * Reveal sets inline left/top on sections while laying out the PDF page (centering in the
 * page box). Those offsets are wrong once the slide lives inside .lectura-print-slide-scaler;
 * reset to (0,0) and fill the native slide box so scaling matches the live presentation.
 */
/** A4 sheet inner size after 7mm padding on .lectura-print-sheet (see CSS), plus 4mm row gap. */
const MM_TO_PX = 96 / 25.4;
function fallbackHandoutRowHeightPx(): number {
  const innerMm = 297 - 14;
  const rowMm = (innerMm - 4) / 2;
  return Math.round(rowMm * MM_TO_PX);
}
function fallbackHandoutRowWidthPx(): number {
  const innerMm = 210 - 14;
  return Math.round(innerMm * MM_TO_PX);
}

function resetRevealPdfOffsetsInsideHandoutScaler(scaler: HTMLElement, sw: number, sh: number): void {
  scaler.querySelectorAll(':scope > .slide-background').forEach((el) => {
    const bg = el as HTMLElement;
    bg.style.setProperty('left', '0', 'important');
    bg.style.setProperty('top', '0', 'important');
    bg.style.setProperty('right', 'auto', 'important');
    bg.style.setProperty('bottom', 'auto', 'important');
    bg.style.setProperty('width', '100%', 'important');
    bg.style.setProperty('height', '100%', 'important');
  });
  scaler.querySelectorAll(':scope > section').forEach((el) => {
    const sec = el as HTMLElement;
    sec.style.setProperty('left', '0', 'important');
    sec.style.setProperty('top', '0', 'important');
    sec.style.setProperty('right', 'auto', 'important');
    sec.style.setProperty('bottom', 'auto', 'important');
    sec.style.setProperty('width', `${sw}px`, 'important');
    sec.style.setProperty('height', `${sh}px`, 'important');
    sec.style.setProperty('margin', '0', 'important');
  });
}

/**
 * Size each slide frame to the largest native-aspect box that fits the row (notes column ~28%).
 * Stores pixel size on the frame so fitSlidesInsideHandoutFrames can scale without relying on
 * getBoundingClientRect() (unreliable in Chrome print preview → tiny scale, thumbnail slides).
 */
function fitHandoutSlideFramesToRows(nativeSlideW: number, nativeSlideH: number): void {
  const aspect = nativeSlideW / Math.max(1, nativeSlideH);
  document.querySelectorAll('.lectura-print-handout-row').forEach((rowEl) => {
    const row = rowEl as HTMLElement;
    const frame = row.querySelector('.lectura-print-slide-frame') as HTMLElement | null;
    if (!frame) return;

    let rw = row.clientWidth;
    let rh = row.clientHeight;
    const page = row.closest('.pdf-page') as HTMLElement | null;

    if (rh < 8 && page) {
      rh = Math.max(60, Math.round(page.getBoundingClientRect().height - 20));
    }
    if (rw < 8 && page) {
      rw = Math.max(80, Math.round(page.getBoundingClientRect().width - 24));
    }

    const fbH = fallbackHandoutRowHeightPx();
    const fbW = fallbackHandoutRowWidthPx();
    if (rh < fbH * 0.65) rh = Math.max(rh, fbH);
    if (rw < fbW * 0.65) rw = Math.max(rw, fbW);

    rw = Math.max(40, rw);
    rh = Math.max(40, rh);

    const slideShare = row.classList.contains('lectura-print-handout-row--no-notes') ? 0.98 : 0.72;
    const maxSlideW = Math.floor(rw * slideShare);
    const maxSlideH = rh;

    let slideW = maxSlideW;
    let slideH = slideW / aspect;
    if (slideH > maxSlideH) {
      slideH = maxSlideH;
      slideW = slideH * aspect;
    }

    const w = Math.round(Math.max(40, slideW));
    const h = Math.round(Math.max(40, slideH));

    frame.style.boxSizing = 'border-box';
    frame.style.width = `${w}px`;
    frame.style.height = `${h}px`;
    frame.style.flex = '0 0 auto';
    frame.style.maxWidth = 'none';
    frame.dataset.lecturaFrameW = String(w);
    frame.dataset.lecturaFrameH = String(h);
  });
}

/**
 * Padding-box size inside the frame border (where the scaler should fit). Prefer deriving
 * from data-lectura-frame-* + getComputedStyle borders — clientWidth/Height often lie in
 * Chrome print preview (tiny scale + empty frame, or wrong scroll metrics).
 */
function readHandoutFrameInnerPx(frame: HTMLElement): { fw: number; fh: number } {
  void frame.offsetWidth;
  const dw = parseFloat(frame.dataset.lecturaFrameW || '');
  const dh = parseFloat(frame.dataset.lecturaFrameH || '');
  if (Number.isFinite(dw) && Number.isFinite(dh) && dw > 0 && dh > 0) {
    const cs = getComputedStyle(frame);
    const bl = parseFloat(cs.borderLeftWidth || '0') || 0;
    const br = parseFloat(cs.borderRightWidth || '0') || 0;
    const bt = parseFloat(cs.borderTopWidth || '0') || 0;
    const bb = parseFloat(cs.borderBottomWidth || '0') || 0;
    const pl = parseFloat(cs.paddingLeft || '0') || 0;
    const pr = parseFloat(cs.paddingRight || '0') || 0;
    const pt = parseFloat(cs.paddingTop || '0') || 0;
    const pb = parseFloat(cs.paddingBottom || '0') || 0;
    return {
      fw: Math.max(1, dw - bl - br - pl - pr),
      fh: Math.max(1, dh - bt - bb - pt - pb)
    };
  }
  const cw = frame.clientWidth;
  const ch = frame.clientHeight;
  if (cw >= 2 && ch >= 2) {
    return { fw: cw, fh: ch };
  }
  const fr = frame.getBoundingClientRect();
  return { fw: Math.max(1, fr.width), fh: Math.max(1, fr.height) };
}

/**
 * Scale background + section together (like Reveal overview): uniform scale so the slide
 * fits the frame (contain). Uses config slide size × scale — same idea as overview thumbnails.
 */
function fitSlidesInsideHandoutFrames(fallbackSlideW: number, fallbackSlideH: number): void {
  document.querySelectorAll('.lectura-print-slide-scaler').forEach((scalerEl) => {
    const scaler = scalerEl as HTMLElement;
    const frame = scaler.closest('.lectura-print-slide-frame') as HTMLElement | null;
    if (!frame) return;

    const dw = parseInt(scaler.dataset.lecturaSlideW || '', 10);
    const dh = parseInt(scaler.dataset.lecturaSlideH || '', 10);
    const sw = Number.isFinite(dw) && dw > 0 ? dw : fallbackSlideW;
    const sh = Number.isFinite(dh) && dh > 0 ? dh : fallbackSlideH;

    resetRevealPdfOffsetsInsideHandoutScaler(scaler, sw, sh);

    scaler.style.transform = '';
    scaler.style.left = '';
    scaler.style.top = '';
    scaler.style.width = `${sw}px`;
    scaler.style.height = `${sh}px`;
    scaler.style.position = 'absolute';
    scaler.style.boxSizing = 'border-box';

    const { fw, fh } = readHandoutFrameInnerPx(frame);
    const scale = Math.min(fw / sw, fh / sh);

    scaler.style.transformOrigin = 'top left';
    /* translate3d(0,0,0) promotes a composited layer — Microsoft Print to PDF often rasterizes that more reliably than plain scale(). */
    scaler.style.transform = `translate3d(0,0,0) scale(${scale})`;
    const wScaled = sw * scale;
    const hScaled = sh * scale;
    scaler.style.left = `${(fw - wScaled) / 2}px`;
    scaler.style.top = `${(fh - hScaled) / 2}px`;
  });
}

type RevealLike = {
  on: (ev: string, fn: () => void) => void;
  getConfig: () => {
    width?: number;
    height?: number;
    margin: number;
    showNotes?: boolean | string;
    pdfPageHeightOffset?: number;
  };
  getComputedSlideSize: (width: number, height: number) => { width: number; height: number };
};

function applyHandoutPageSheetSize(): void {
  const existing = document.getElementById('lectura-reveal-print-page-size');
  existing?.remove();
  const style = document.createElement('style');
  style.id = 'lectura-reveal-print-page-size';
  /*
   * margin: 0 — if @page has margins while .lectura-print-sheet is 210×297mm, the browser
   * shrinks the whole layout to fit the *inset* printable area → tiny slides; users crank
   * print scale to ~150% and transform-based slide scaling breaks. Margins live as padding
   * on the sheet in CSS instead.
   */
  /*
   * Injected last at pdf-ready so it wins over theme order. Fills the printable area in Chrome
   * Save as PDF (avoids grey margins when outer rules constrain width).
   */
  style.textContent = `@page { size: A4 portrait; margin: 0; }
@media print {
  html.print-pdf.lectura-print-handout,
  html.print-pdf.lectura-print-handout body {
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
    background: #ffffff !important;
    background-color: #ffffff !important;
  }
  html.print-pdf.lectura-print-handout #lectura-root {
    width: 100% !important;
    max-width: none !important;
    background: #ffffff !important;
    background-color: #ffffff !important;
  }
  html.print-pdf.lectura-print-handout .rf-slides-chat-wrap {
    width: 100% !important;
    max-width: none !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
    background-color: #ffffff !important;
  }
  html.print-pdf.lectura-print-handout .rf-slides-chat-wrap .reveal {
    container-type: normal !important;
    width: 100% !important;
    max-width: none !important;
    background: #ffffff !important;
    background-color: #ffffff !important;
  }
  html.print-pdf.lectura-print-handout .reveal-viewport,
  html.print-pdf.lectura-print-handout .reveal .slides {
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
    background-color: #ffffff !important;
  }
  html.print-pdf.lectura-print-handout .lectura-print-sheet {
    background: #ffffff !important;
    background-color: #ffffff !important;
  }
  html.print-pdf.lectura-print-handout .lectura-print-sheet .pdf-page,
  html.print-pdf.lectura-print-handout .lectura-print-handout-row {
    background: #ffffff !important;
    background-color: #ffffff !important;
  }
  html.print-pdf.lectura-print-handout .lectura-print-sheet {
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    height: 297mm !important;
    min-height: 297mm !important;
  }
}`;
  document.head.appendChild(style);
}

/**
 * After Reveal builds the PDF layout, convert to handout rows and set @page size.
 */
export function installRevealPrintHooks(Reveal: RevealLike): void {
  if (!isRevealPrintPdfMode() || !Reveal || typeof Reveal.on !== 'function') return;

  Reveal.on('pdf-ready', () => {
    const cfg = Reveal.getConfig?.();
    if (!cfg) return;

    document.body.style.removeProperty('height');

    const size = Reveal.getComputedSlideSize(window.innerWidth, window.innerHeight);
    const slideW = Math.max(1, Math.floor(size.width));
    const slideH = Math.max(1, Math.floor(size.height));
    const nativeW =
      typeof cfg.width === 'number' && cfg.width > 0 ? Math.floor(cfg.width) : slideW;
    const nativeH =
      typeof cfg.height === 'number' && cfg.height > 0 ? Math.floor(cfg.height) : slideH;
    const pageH = Math.floor(size.height * (1 + cfg.margin));

    /* Handout slide frame: % of row width (two slides per A4 page); avoid Reveal PDF page pixel width (~1330px). */
    document.documentElement.style.setProperty('--lectura-print-page-w', '42%');
    document.documentElement.style.setProperty('--lectura-print-page-h', `${pageH}px`);
    document.documentElement.style.setProperty('--lectura-print-native-w', String(nativeW));
    document.documentElement.style.setProperty('--lectura-print-native-h', String(nativeH));

    if (cfg.showNotes) {
      document.querySelectorAll('.speaker-notes-pdf').forEach((el) => {
        el.innerHTML = stripLecturaCodewordsFromHtml(el.innerHTML);
      });
    }

    restructurePdfPagesToHandout(nativeW, nativeH);
    groupHandoutPagesIntoSheets();
    clearRevealPdfPageHeights();
    document.documentElement.classList.add('lectura-print-handout');
    document.documentElement.classList.remove('lectura-print-pdf-split');

    const runLayout = (): void => {
      fitHandoutSlideFramesToRows(nativeW, nativeH);
      fitSlidesInsideHandoutFrames(nativeW, nativeH);
      applyHandoutPageSheetSize();
    };
    handoutLayoutRunner = runLayout;

    installPrintPdfToolbar();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        runLayout();
        requestAnimationFrame(runLayout);
      });
    });
    window.setTimeout(() => {
      runLayout();
    }, 100);

    let resizeRaf = 0;
    const onResize = (): void => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = 0;
        runLayout();
      });
    };
    window.addEventListener('resize', onResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onResize);
    }

    const relayoutForPrint = (): void => {
      refreshHandoutLeadCover();
      runLayout();
      requestAnimationFrame(() => {
        runLayout();
        requestAnimationFrame(runLayout);
      });
      window.setTimeout(() => runLayout(), 0);
      window.setTimeout(() => runLayout(), 100);
    };
    window.addEventListener('beforeprint', relayoutForPrint);
    window.addEventListener('afterprint', relayoutForPrint);
    const mql = window.matchMedia('print');
    const onPrintMq = (): void => {
      if (mql.matches) relayoutForPrint();
    };
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onPrintMq);
    } else {
      mql.addListener(onPrintMq);
    }
  });
}

export function getRevealPrintPdfRevealOptions(): {
  showNotes: boolean;
  keyboardCondition: () => boolean;
} | Record<string, never> {
  if (!isRevealPrintPdfMode()) return {};
  return {
    showNotes: true,
    keyboardCondition: () => false
  };
}

if (typeof window !== 'undefined') {
  const w = window as unknown as {
    installRevealPrintHooks?: typeof installRevealPrintHooks;
    isRevealPrintPdfMode?: typeof isRevealPrintPdfMode;
    applyPrintPdfNotesFilter?: typeof applyPrintPdfNotesFilter;
  };
  w.installRevealPrintHooks = installRevealPrintHooks;
  w.isRevealPrintPdfMode = isRevealPrintPdfMode;
  w.applyPrintPdfNotesFilter = applyPrintPdfNotesFilter;
}
